import type { UseQueryResult } from "@tanstack/react-query";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { findAppsQueryKeyFactory } from "app/pages/sales-intelligence/pages/find-apps/query/queryKeyFactory";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { selectAndroidRequestDto, selectIosRequestDto } from "app/pages/sales-intelligence/store/find-apps/selectors";
import { AppsSourceType } from "app/pages/sales-intelligence/store/find-apps/types/appsSourceType";
import type { IFindAppsResponseDto } from "app/pages/sales-intelligence/store/find-apps/types/dto";

export function useAndroidAppsData<T = IFindAppsResponseDto>(
    select?: (data: IFindAppsResponseDto) => T,
): UseQueryResult<T, unknown> {
    const dto = useSelector(selectAndroidRequestDto);

    return useQuery<IFindAppsResponseDto, unknown, T>({
        staleTime: Number.POSITIVE_INFINITY,
        queryKey: findAppsQueryKeyFactory.appData(AppsSourceType.GoogleStore, dto),
        placeholderData: keepPreviousData,
        select,
        queryFn: ({ signal }) => {
            return salesApiService.findApps.fetchAndroidAppsData(dto, { cancellation: signal });
        },
    });
}

export function useIosAppsData<T = IFindAppsResponseDto>(
    select?: (data: IFindAppsResponseDto) => T,
): UseQueryResult<T, unknown> {
    const dto = useSelector(selectIosRequestDto);

    return useQuery<IFindAppsResponseDto, unknown, T>({
        staleTime: Number.POSITIVE_INFINITY,
        queryKey: findAppsQueryKeyFactory.appData(AppsSourceType.AppleStore, dto),
        placeholderData: keepPreviousData,
        select,
        queryFn: ({ signal }) => {
            return salesApiService.findApps.fetchIosAppsData(dto, { cancellation: signal });
        },
    });
}
